import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Fade,
  Skeleton,
} from "@mui/material";
import axiosInstance from "../../../services/axiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import LeadRevenueTable from "./SingleLeadRevenueTable";
import AdminSideNav from "../../../adminComponents/sidenav";
import AdminHeader from "../../../adminComponents/header";
import { formatDate as centralFormatDate } from "../../../utils/formatters";

const InfoRow = ({ icon: Icon, label, value, color = "primary" }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}>
    <Avatar
      sx={{
        bgcolor: "white",
        color: `${color}.main`,
        width: 35,
        height: 35,
      }}
    >
      <Icon fontSize="small" />
    </Avatar>
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        {value || "-"}
      </Typography>
    </Box>
  </Box>
);

const SingleLead = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const getUserDetails = async () => {
      try {
        const res = await axiosInstance.get(`${apiEndPointsConfig.singlLead}/${id}`);
        setLead(res?.data);
      } catch (error) {
        console.error("Error fetching lead details:", error);
      } finally {
        setLoading(false);
      }
    };

    getUserDetails();
  }, [id, token]);


  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{ p: 3, borderRadius: 3, bgcolor: "background.paper" }}
        >
          <Skeleton
            variant="rectangular"
            height={60}
            sx={{ mb: 3, borderRadius: 2 }}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Skeleton
                variant="rectangular"
                height={300}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton
                variant="rectangular"
                height={300}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Skeleton
                variant="rectangular"
                height={200}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  }

  const { data, faqs } = lead || {};

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          ml: { xs: "0px", md: "200px" },
        }}
      >
        <AdminSideNav />
        <Box
          sx={{
            display: {
              xs: "none",
              md: "initial",
            },
          }}
        >
          <AdminHeader title={"Lead Details"} />
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
          }}
        >
          <Fade in timeout={600}>
            <Box
              sx={{
                p: { xs: 2, md: 3 },
                minHeight: "100vh",
                bgcolor: "grey.50",
              }}
            >
              <Box sx={{ maxWidth: 1400, mx: "auto" }}>
                {/* Header */}

                {!data ? (
                  <Paper
                    elevation={0}
                    sx={{ p: 6, borderRadius: 3, textAlign: "center" }}
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No Data Found
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Unable to load lead information
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={3}>
                    {/* Personal Information Card */}
                    <Grid item xs={12} md={6}>
                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          height: "100%",
                          border: "1px solid",
                          borderColor: "divider",
                          transition: "all 0.3s",
                          "&:hover": {
                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                            transform: "translateY(-4px)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 3,
                            }}
                          >
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                Personal Information
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Contact details and identity
                              </Typography>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Stack spacing={0.5}>
                            <InfoRow
                              icon={PersonIcon}
                              label="Account Name"
                              value={data?.name}
                              color="primary"
                            />
                            <InfoRow
                              icon={EmailIcon}
                              label="Email Address"
                              value={data?.email}
                              color="secondary"
                            />
                            <InfoRow
                              icon={PhoneIcon}
                              label="Mobile Number"
                              value={data?.phoneNumber}
                              color="success"
                            />
                            <InfoRow
                              icon={CalendarTodayIcon}
                              label="Registration Date"
                              value={centralFormatDate(data?.createdAt)}
                              color="info"
                            />
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Business Information Card */}
                    <Grid item xs={12} md={6}>
                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          height: "100%",
                          border: "1px solid",
                          borderColor: "divider",
                          transition: "all 0.3s",
                          "&:hover": {
                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                            transform: "translateY(-4px)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 3,
                            }}
                          >
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                Business Information
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Company and industry details
                              </Typography>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Stack spacing={0.5}>
                            <InfoRow
                              icon={BusinessIcon}
                              label="Company Name"
                              value={data?.companyName}
                              color="success"
                            />
                            <InfoRow
                              icon={CategoryIcon}
                              label="Industry Type"
                              value={data?.industryType}
                              color="warning"
                            />
                            <InfoRow
                              icon={ContactPhoneIcon}
                              label="DID Number"
                              value={data?.customerCareNo}
                              color="error"
                            />

                            <InfoRow
                              icon={AttachMoneyIcon}
                              label="Subscription Amount"
                              value={data?.subscriptionAmount}
                              color="success"
                            />
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* FAQs Card */}
                    <Grid item xs={12}>
                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: "1px solid",
                          borderColor: "divider",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            p: 3,
                            // background:
                            //   "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                            color: "black",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            {/* <Avatar
                        sx={{
                          bgcolor: "rgba(255,255,255,0.3)",
                          width: 48,
                          height: 48,
                        }}
                      >
                        <HelpOutlineIcon />
                      </Avatar> */}
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                Frequently Asked Questions
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {Array.isArray(faqs) && faqs.length > 0
                                  ? `${faqs.length} question${faqs.length !== 1 ? "s" : ""
                                  } available`
                                  : "No questions available"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <CardContent sx={{ p: 3 }}>
                          {Array.isArray(faqs) && faqs.length > 0 ? (
                            <Stack spacing={2}>
                              {faqs.map((faq, index) => (
                                <Accordion
                                  key={index}
                                  elevation={0}
                                  sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: "12px !important",
                                    "&:before": { display: "none" },
                                    overflow: "hidden",
                                  }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ArrowDropDownIcon />}
                                    sx={{
                                      bgcolor: "grey.50",
                                      "&:hover": { bgcolor: "grey.100" },
                                      borderRadius: "12px",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                      }}
                                    >
                                      <Chip
                                        label={`Q${index + 1}`}
                                        size="small"
                                        color="primary"
                                        sx={{ fontWeight: 600 }}
                                      />
                                      <Typography fontWeight={500}>
                                        {faq?.faq_title || "Untitled Question"}
                                      </Typography>
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails
                                    sx={{ p: 3, bgcolor: "background.paper" }}
                                  >
                                    <Typography
                                      color="text.secondary"
                                      lineHeight={1.7}
                                    >
                                      {faq?.faq_answer || "No answer provided."}
                                    </Typography>
                                  </AccordionDetails>
                                </Accordion>
                              ))}
                            </Stack>
                          ) : (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                              <HelpOutlineIcon
                                sx={{
                                  fontSize: 48,
                                  color: "text.disabled",
                                  mb: 2,
                                }}
                              />
                              <Typography color="text.secondary">
                                No FAQs available for this lead.
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                      <Box mt={3}>
                        {lead?.revenue == 0 ? (
                          ""
                        ) : (
                          <LeadRevenueTable revenue={lead?.revenue} />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Box>
          </Fade>
        </Box>
      </Box>
    </Box>
  );
};

export default SingleLead;
